import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../slices/authSlice";
import LoginModal from "./LoginModal";
import RegisterModal from "./RegisterModal";
import SearchModal from "./SearchModal"; // ✅ new import
import { useSettings } from "../context/SettingsContext";
import {
  Truck,
  ShoppingBag,
  Menu,
  X,
  Clock,
  Utensils,
  ChevronDown,
  User,
  LogOut,
  Search, // ✅ import Search icon
} from "lucide-react";
import { toast } from "react-toastify";
import { useNavigate, Link } from "react-router-dom";

const Topbar = () => {
  const { user, token } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const [loginOpen, setLoginOpen] = useState(false);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false); // ✅ state for search modal
  const [offsetY, setOffsetY] = useState(0);
  const [timeLeft, setTimeLeft] = useState(null);
  const [tableName, setTableName] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const navigate = useNavigate();
  const { settings, loading } = useSettings();

const pages = [
  {
    name: "Home",
    href:
      settings?.is_stripedown == 1 && settings?.other_site
        ? settings.other_site
        : window.BASE_URL,
    external: settings?.is_stripedown == 1 && settings?.other_site,
  },
  { name: "Menu", href: "/", external: false },

  ...(settings?.is_contact_us == 1
    ? [
        {
          name: "Contact Us",
          href:
            settings?.is_contact_us_link == 1
              ? settings?.contact_us_link
              : "/contact-us",
          external: settings?.is_contact_us_link == 1,
        },
      ]
    : []),

  ...(settings?.is_about_us == 1
    ? [
        {
          name: "About Us",
          href:
            settings?.is_about_us_link == 1
              ? settings?.about_us_link
              : "/about-us",
          external: settings?.is_about_us_link == 1,
        },
      ]
    : []),

  ...(settings?.is_reservation == 1
    ? [{ name: "Reservation", href: "/reservation", external: false }]
    : []),

  ...(settings?.is_eventpage == 1
    ? [{ name: "Events", href: window.BASE_URL + "/events", external: true }]
    : []),

  ...(settings?.is_gift_voucher_page == 1
    ? [{ name: "Gift Voucher", href: "/gift-voucher", external: false }]
    : []),
];



  // Parallax scroll
  useEffect(() => {
    const handleScroll = () => setOffsetY(window.scrollY);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Timer logic
  useEffect(() => {
    const dineinSession = JSON.parse(localStorage.getItem("dinein"));
    if (!dineinSession || !dineinSession.expiresAt) {
      setTimeLeft(null);
      setTableName(null);
      return;
    }

    setTableName(dineinSession.tableName || null);

    const updateTimer = () => {
      const now = Date.now();
      const diff = dineinSession.expiresAt - now;

      if (diff <= 0) {
        localStorage.removeItem("dinein");
        localStorage.removeItem("apptype");
        localStorage.removeItem("tableId");
        localStorage.removeItem("tableName");
        setTimeLeft(null);
        setTableName(null);
        return;
      }

      const minutes = Math.floor(diff / 1000 / 60);
      const seconds = Math.floor((diff / 1000) % 60);
      setTimeLeft(
        `${minutes.toString().padStart(2, "0")}:${seconds
          .toString()
          .padStart(2, "0")}`
      );
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, []);

  // 🚪 Logout handler
  const handleLogout = () => {
    setDropdownOpen(false);
    setShowLogoutConfirm(true);
  };

  const confirmLogout = () => {
    setShowLogoutConfirm(false);
    dispatch(logout());
    toast.success("You’ve been logged out successfully 👋");
    navigate("/");
  };

  const cancelLogout = () => {
    setShowLogoutConfirm(false);
  };

  return (
    <header
      className="relative w-full h-72 sm:h-70 md:h-80 lg:h-96 overflow-hidden shadow-md bg-cover bg-center"
      style={{
        backgroundImage: !tableName ? `url(${settings?.banner || ""})` : `url(${settings?.banner_dinein || ""})`,
        backgroundPositionY: `${offsetY * 0.5}px`,
      }}
    >
      <div className="absolute inset-0 bg-black/60" />
      <div className="relative z-10 flex flex-col h-full px-4">
        {/* === Top Row === */}
        <div className="flex justify-between items-center mt-3">
          {/* Logo */}
<div className="flex items-center gap-2">
  {tableName ? (
    // 🚫 Dine-in active → Disable click
    <img
      src={settings?.logo}
      alt="Logo"
      className="h-[5rem] sm:h-[7rem] w-auto object-contain drop-shadow-lg opacity-90 cursor-not-allowed"
      title="Navigation disabled during dine-in session"
    />
  ) : settings?.is_stripedown == 1 && settings?.other_site ? (
    <a
      href={settings.other_site}
      target="_blank"
      rel="noopener noreferrer"
    >
      <img
        src={settings?.logo}
        alt="Logo"
        className="h-[5rem] sm:h-[7rem] w-auto object-contain drop-shadow-lg cursor-pointer transition-transform duration-300 hover:scale-105"
      />
    </a>
  ) : (
    <a href={window.BASE_URL}>
      <img
        src={settings?.logo}
        alt="Logo"
        className="h-[5rem] sm:h-[7rem] w-auto object-contain drop-shadow-lg cursor-pointer transition-transform duration-300 hover:scale-105"
      />
    </a>
  )}
</div>

          {/* ✅ Desktop Navigation */}

       {/* ✅ Desktop Navigation */}
{!tableName && (
  <nav className="hidden sm:flex items-center gap-6 text-sm font-medium text-gray-100">
    {pages.map((page) =>
      page.external ? (
        <a
          key={page.name}
          href={page.href}
          target="_blank"
          rel="noopener noreferrer"
          className="relative group"
          style={{fontSize:"18px"}}
        >
          <span
            className="transition-colors"
            style={{ color: settings?.cart_header || "#ffffff" }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.color =
                settings?.cart_header_hover || "#f59e0b")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.color =
                settings?.cart_header || "#ffffff")
            }
          >
            {page.name}
          </span>

          <span
            className="absolute left-0 bottom-0 w-0 h-[2px] transition-all duration-300 group-hover:w-full"
            style={{
              backgroundColor: settings?.cart_header_hover || "#f59e0b",
            }}
          />
        </a>
      ) : (
        <Link key={page.name} to={page.href} className="relative group"     style={{fontSize:"18px"}}>
          <span
            className="transition-colors"
            style={{ color: settings?.cart_header || "#ffffff" }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.color =
                settings?.cart_header_hover || "#f59e0b")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.color =
                settings?.cart_header || "#ffffff")
            }
          >
            {page.name}
          </span>

          <span
            className="absolute left-0 bottom-0 w-0 h-[2px] transition-all duration-300 group-hover:w-full"
            style={{
              backgroundColor: settings?.cart_header_hover || "#f59e0b",
            }}
          />
        </Link>
      )
    )}
  </nav>
)}


          {/* Right Side Controls */}
          <div className="flex items-center gap-3 relative">
            {/* Timer */}
            {timeLeft && (
              <div className="flex items-center gap-2 bg-emerald-600 text-white px-3 py-1 rounded-md shadow-md">
                <Clock size={16} />
                <span className="text-sm font-semibold">{timeLeft}</span>
              </div>
            )}

            {/* 🔍 Search Icon */}
            <button
              onClick={() => setSearchOpen(true)}
              className="p-2 rounded-md bg-black/40 border border-gray-500 text-white hover:bg-black/60 transition"
              title="Search"
            >
              <Search size={18} />
            </button>

            {/* ✅ User Dropdown */}
            {!timeLeft && user && token && (
              <div className="relative">
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="flex items-center gap-2 bg-black/40 border border-gray-500 px-3 py-1.5 rounded-md text-white text-xs font-medium hover:bg-black/60 transition"
                >
                  <User size={16} />
                  {user.name || "User"}
                  <ChevronDown
                    size={14}
                    className={`transition-transform ${dropdownOpen ? "rotate-180" : "rotate-0"
                      }`}
                  />
                </button>

                {dropdownOpen && (
                  <div className="absolute right-0 mt-2 w-40 bg-white rounded-md shadow-lg border border-gray-200 overflow-hidden z-50">
                    <Link
                      to="/my-account"
                      onClick={() => setDropdownOpen(false)}
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-amber-50 hover:text-amber-700 transition"
                    >
                      My Account
                    </Link>

                    {localStorage.getItem('apptype') != 'app' &&
                      <button
                        onClick={handleLogout}
                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition flex items-center gap-2"
                      >
                        <LogOut size={14} />
                        Logout
                      </button>
                    }
                  </div>
                )}
              </div>
            )}

            {/* Login/Register */}
            {!timeLeft && !user && localStorage.getItem('apptype') != 'app' && (
              <>
                <button
                  onClick={() => setLoginOpen(true)}
                  className="px-3 py-1 text-xs rounded-md bg-black/60 border border-gray-400 text-white hover:bg-black/80 transition"
                >
                  Login
                </button>
                <button
                  onClick={() => setRegisterOpen(true)}
                  className="px-3 py-1 text-xs rounded-md bg-black/60 border border-gray-400 text-white hover:bg-black/80 transition"
                >
                  Register
                </button>
              </>
            )}

            {/* Mobile Menu Button */}
            {(!timeLeft && localStorage.getItem('apptype') != 'app') && (
              <button
                className="p-2 rounded-md text-white hover:bg-white/20 transition sm:hidden"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu size={24} />
              </button>
            )}
          </div>
        </div>

        {/* === Center Title === */}
        <div className="flex flex-col items-center justify-center flex-grow text-center">
          <h1
            className="text-xl md:text-3xl font-extrabold text-white drop-shadow-lg tracking-wide mb-4"
            style={{ transform: `translateY(${offsetY * 0.3}px)` }}
          >
            {loading ? "Loading..." : settings?.resturant_name || ""}
          </h1>
  {timeLeft && tableName &&

  <>
          <p
            className="text-xl md:text-1xl font-extrabold text-white drop-shadow-lg tracking-wide mb-4"
            style={{ transform: `translateY(${offsetY * 0.3}px)`, fontSize:"13px", fontWeight:"600" }}
          >
            {settings?.text}
          </p>


          </>
}
        <div
  className="flex flex-wrap justify-center items-center gap-4 sm:gap-6 text-center"
  style={{ transform: `translateY(${offsetY * 0.5}px)` }}
>
            {timeLeft && tableName ? (

               <>
              <div className="flex items-center gap-2 bg-white/90 backdrop-blur-md px-5 py-2 rounded-full shadow-md">
                <Utensils size={18} className="text-emerald-600" />
                <span className="text-sm font-semibold text-gray-800">
                  {tableName}
                </span>
              </div>
    {settings?.is_dinein_order == 1 ? (
  <span className="inline-flex items-center px-3 py-1 text-sm font-semibold text-green-700 bg-green-100 rounded-full">
    🟢 We are accepting orders
  </span>
) : (
  <span className="inline-flex items-center px-3 py-1 text-sm font-semibold text-red-700 bg-red-100 rounded-full">
    🔴 We are not accepting orders
  </span>
)}

              </>
            ) : (
              <>

              
                {settings?.delivery_time ? (
                  <div className="flex items-center gap-2 bg-white/90 backdrop-blur-md px-4 py-2 rounded-full shadow-md">
                    <Truck size={18} className="text-emerald-600" />
                    <span className="text-xs font-semibold text-gray-700 uppercase">
                      Delivery
                    </span>


                    {settings?.delivery_status == 'Closed' ?
                      <span className=" inline-block bg-gradient-to-r from-emerald-500 to-green-600 text-white text-xs font-bold px-2 py-0.5 rounded-full shadow-sm">
                        Closed
                      </span>

                      :

                      <span className=" inline-block bg-gradient-to-r from-emerald-500 to-green-600 text-white text-xs font-bold px-2 py-0.5 rounded-full shadow-sm">
                       Open
                      </span>
                    }

                    {settings?.is_delivery_off == 0 ?
                      <span className=" inline-block bg-gradient-to-r from-emerald-500 to-green-600 text-white text-xs font-bold px-2 py-0.5 rounded-full shadow-sm">
                        Off
                      </span>

                      :

                      <span className=" inline-block bg-gradient-to-r from-emerald-500 to-green-600 text-white text-xs font-bold px-2 py-0.5 rounded-full shadow-sm">
                        {settings.delivery_time} min
                      </span>
                    }
                  </div>
                ) : ''}
                {settings?.pickup_time && (
                  <div className="flex items-center gap-2 bg-white/90 backdrop-blur-md px-4 py-2 rounded-full shadow-md">
                    <ShoppingBag size={18} className="text-blue-600" />
                    <span className="text-xs font-semibold text-gray-700 uppercase">
                      Pickup
                    </span>

                      {settings?.pickup_status == 'Closed' ?
                      <span className=" inline-block bg-gradient-to-r from-emerald-500 to-green-600 text-white text-xs font-bold px-2 py-0.5 rounded-full shadow-sm">
                        Closed
                      </span>

                      :

                      <span className=" inline-block bg-gradient-to-r from-emerald-500 to-green-600 text-white text-xs font-bold px-2 py-0.5 rounded-full shadow-sm">
                       Open
                      </span>
                    }


                    <span className=" inline-block bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-xs font-bold px-2 py-0.5 rounded-full shadow-sm">
                      {settings.pickup_time} min
                    </span>
                  </div>
                )}
              </>
            )}

         
          </div>
        </div>
      </div>

      {/* === Logout Confirm Modal === */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm z-50 animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl w-80 p-6 transform animate-scaleIn text-center">
            <div className="text-amber-500 mb-3 flex justify-center">
              <LogOut size={40} className="drop-shadow" />
            </div>
            <h2 className="text-lg font-bold text-gray-800 mb-2">
              Confirm Logout
            </h2>
            <p className="text-sm text-gray-500 mb-6">
              Are you sure you want to log out?
            </p>
            <div className="flex justify-center gap-4">
              <button
                onClick={cancelLogout}
                className="px-4 py-2 rounded-full bg-gray-200 text-gray-700 text-sm font-medium hover:bg-gray-300 transition"
              >
                Cancel
              </button>
              <button
                onClick={confirmLogout}
                className="px-4 py-2 rounded-full bg-gradient-to-r from-red-500 to-rose-600 text-white text-sm font-medium shadow-md hover:opacity-90 transition"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}

      {/* === Search Modal === */}
      {searchOpen && <SearchModal onClose={() => setSearchOpen(false)} />}

      {/* === Animations === */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleIn {
          from { transform: scale(0.8); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        .animate-fadeIn {
          animation: fadeIn 0.25s ease-out forwards;
        }
        .animate-scaleIn {
          animation: scaleIn 0.25s ease-out forwards;
        }
      `}</style>

 {/* === Mobile Sidebar === */}
{sidebarOpen && (
  <div className="fixed inset-0 bg-black/60 z-50 flex">
    <div className="w-64 bg-white h-full shadow-lg p-5 flex flex-col">
      <button
        className="self-end mb-4 text-gray-600 hover:text-black"
        onClick={() => setSidebarOpen(false)}
      >
        <X size={22} />
      </button>

      <nav className="flex flex-col gap-4">
        {pages.map((page) =>
          page.external ? (
            <a
              key={page.name}
              href={page.href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setSidebarOpen(false)}
              className="text-base font-medium text-gray-800 hover:text-emerald-600 transition"
            >
              {page.name}
            </a>
          ) : (
            <Link
              key={page.name}
              to={page.href}
              onClick={() => setSidebarOpen(false)}
              className="text-base font-medium text-gray-800 hover:text-emerald-600 transition"
            >
              {page.name}
            </Link>
          )
        )}
      </nav>
    </div>

    <div className="flex-grow" onClick={() => setSidebarOpen(false)} />
  </div>
)}

      {/* === Auth Modals === */}
      {loginOpen && (
        <LoginModal
          onClose={() => setLoginOpen(false)}
          onSwitchToRegister={() => {
            setLoginOpen(false);
            setRegisterOpen(true);
          }}
        />
      )}
      {registerOpen && (
        <RegisterModal
          onClose={() => setRegisterOpen(false)}
          onSwitchToLogin={() => {
            setRegisterOpen(false);
            setLoginOpen(true);
          }}
        />
      )}
    </header>
  );
};

export default Topbar;
